import { searchConfig } from '@cocoonspace/shared/config/search-config'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { type MouseEventHandler, type PropsWithChildren, useMemo } from 'react'
import type { TimeSlot } from '../../templates/booking-stepper'
import { GridDurationRow } from './grid-duration-row'
import {
	STEP_TIME_MINUTES,
	TOTAL_HEIGHT,
	type TimeslotBounds,
	calculateTimeFromY,
	snapToGrid,
} from './helpers'

interface DraggableTimeslotContainerProps extends PropsWithChildren {
	calendar: { available: boolean; hour: string }[]
	bounds: TimeslotBounds
	value: TimeSlot
	onTimeChange: (start: string, end: string) => void
}

export const DraggableTimeslotContainer = ({
	calendar,
	bounds,
	children,
	value,
	onTimeChange,
}: DraggableTimeslotContainerProps) => {
	const handleContainerClick: MouseEventHandler<HTMLDivElement> = (e) => {
		const containerRect = e.currentTarget.getBoundingClientRect()
		const clickY = e.clientY - containerRect.top

		let newStartY = snapToGrid(clickY - bounds.height / 2)

		newStartY = Math.max(0, Math.min(newStartY, TOTAL_HEIGHT - bounds.height))

		onTimeChange(
			calculateTimeFromY(newStartY),
			calculateTimeFromY(newStartY + bounds.height),
		)
	}

	const grid = useMemo(() => {
		const start = dateTimeUtils.utils.getTimeAsMinutes(
			searchConfig.timeSlot.min,
		)
		const end = dateTimeUtils.utils.getTimeAsMinutes(searchConfig.timeSlot.max)

		return Array.from({ length: (end - start) / STEP_TIME_MINUTES + 1 }).map(
			(_, i) => {
				const hour = dateTimeUtils
					.duration(start + i * STEP_TIME_MINUTES, 'minutes')
					.format('HH[:]mm')

				return {
					hour,
					available:
						calendar?.find((timeslot) => timeslot.hour === hour)?.available ||
						false,
				}
			},
		)
	}, [calendar])

	return (
		<div
			className='relative ml-[40px] cursor-pointer'
			style={{ height: TOTAL_HEIGHT }}
			onClick={handleContainerClick}
		>
			{grid.map((row) => (
				<GridDurationRow
					key={row.hour}
					rowTime={row.hour}
					isAvailable={row.available}
					value={value}
				/>
			))}

			{children}
		</div>
	)
}
